'use client';

import classnames from 'classnames';
import React, { useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { useOrientation, useWindowSize } from 'react-use';

import { useIsMobile } from '@cineamo/ui/src/helpers/screen-type';

import { IconPlayButton56 } from '@cineamo/ui/src/icons/ic-playButton';
import { BackdropAndPosterCompositeImage } from '@cineamo/ui/src/image/BackdropAndPosterCompositeImage';
import FullScreenModal from '@cineamo/ui/src/modals/FullScreenModal';

import { useAnalytics } from './providers/analyticsProvider';

type AllowedPropertyValues = string | number | boolean | null;

type TrailerAndPosterCompositeImageProps = {
    title: string;
    backdropImage: string;
    backdropBlurDataImage?: string;
    posterImage: string;
    posterBlurDataImage?: string;
    trailerUrl?: string;
    showPoster?: boolean;
    hideTrailer?: boolean;
    className?: string;
    /** Passed through to `BackdropAndPosterCompositeImage` to enable responsive image loading. */
    sizes?: string;
    trackingData?: Record<string, AllowedPropertyValues>;
    onTrailerPlay?: () => void;
    /**
     * Fired at most once per play session — when the viewer reaches the completion
     * threshold (85% played) or the video ends, whichever comes first.
     */
    onTrailerFinished?: () => void;
};

const TRAILER_FINISHED_THRESHOLD = 0.85;

export function TrailerAndPosterCompositeImage({
    title,
    backdropImage,
    backdropBlurDataImage,
    posterImage,
    posterBlurDataImage,
    trailerUrl,
    showPoster,
    hideTrailer,
    className,
    sizes,
    trackingData,
    onTrailerPlay,
    onTrailerFinished
}: TrailerAndPosterCompositeImageProps) {
    const { track } = useAnalytics();

    const isMobile = useIsMobile('lg');
    const orientation = useOrientation();
    const { width, height } = useWindowSize();

    const [trailerModalIsOpen, setTrailerModalIsOpen] = useState<boolean>(false);
    const headerVideoPlayerRef = useRef(null);
    // Guards onTrailerFinished so it fires at most once per play session.
    // react-player's onProgress ticks ~once a second, so without this flag the
    // callback would fire on every tick past the threshold.
    const finishedFiredRef = useRef(false);

    const fireFinishedOnce = () => {
        if (!finishedFiredRef.current) {
            finishedFiredRef.current = true;
            onTrailerFinished?.();
        }
    };

    const playerSize = useMemo(() => {
        const ratio = 540 / 960;
        const playerHeightOnMobile = width * ratio + 100;
        return {
            width: isMobile ? width : 960,
            height: isMobile ? (orientation.type === 'landscape-primary' ? height : playerHeightOnMobile) : 540
        };
    }, [orientation.type, isMobile, width, height]);

    const showTrailerButton = hideTrailer !== true && trailerUrl && !trailerModalIsOpen;

    return (
        <div
            className={classnames({
                'pointer-events-auto': trailerModalIsOpen
            })}
            onClick={() => {
                if (trailerModalIsOpen) setTrailerModalIsOpen(false);
            }}>
            {trailerModalIsOpen && trailerUrl && (
                <FullScreenModal
                    modalIsOpen={trailerModalIsOpen}
                    setModalIsOpen={setTrailerModalIsOpen}
                    onClose={null}>
                    <ReactPlayer
                        url={trailerUrl}
                        playing={trailerModalIsOpen}
                        onEnded={() => {
                            fireFinishedOnce();
                            setTrailerModalIsOpen(false);
                        }}
                        onProgress={({ played }) => {
                            if (played >= TRAILER_FINISHED_THRESHOLD) {
                                fireFinishedOnce();
                            }
                        }}
                        controls={true}
                        ref={headerVideoPlayerRef}
                        width={playerSize.width}
                        height={playerSize.height}
                    />
                </FullScreenModal>
            )}
            <BackdropAndPosterCompositeImage
                title={title}
                backdropImage={backdropImage}
                backdropBlurDataImage={backdropBlurDataImage}
                posterImage={posterImage}
                posterBlurDataImage={posterBlurDataImage}
                showPoster={showPoster}
                sizes={sizes}
                className={classnames('h-210 lg:h-240 lg:aspect-[16/9]', className)}>
                {showTrailerButton && (
                    <div className="flex flex-auto items-center justify-center">
                        <IconPlayButton56
                            className="pointer-events-auto clickable-element text-black/50"
                            foregroundcolor="white"
                            role="button"
                            aria-label="Play Trailer Button"
                            onClick={(event) => {
                                event.stopPropagation();
                                track('Movie Trailer Played', trackingData);
                                finishedFiredRef.current = false;
                                onTrailerPlay?.();
                                setTrailerModalIsOpen(true);
                            }}
                        />
                    </div>
                )}
            </BackdropAndPosterCompositeImage>
        </div>
    );
}
